/* © CNES */
import {
  PaginatorNextPageLinkOptions,
  PaginatorPrevPageLinkOptions,
} from 'primereact/paginator';
import { Ripple } from 'primereact/ripple';
import { classNames } from 'primereact/utils';

/**
 * CustomButton Props.
 */
interface CustomButtonProps {
  options: PaginatorPrevPageLinkOptions | PaginatorNextPageLinkOptions;
  label: string;
}

// PaginatorTemplate: Configuration object for customizing the layout and behavior of the Paginator component.
//
// Properties:
// - layout: A string defining the layout of the paginator. It specifies the order and inclusion of elements such as:
//   - 'PrevPageLink': Button for navigating to the previous page.
//   - 'PageLinks': Links to individual pages.
//   - 'NextPageLink': Button for navigating to the next page.
//   - 'RowsPerPageDropdown': Dropdown for selecting the number of rows per page.
//   - 'CurrentPageReport': Display of the current page and total pages.
//
// - PrevPageLink: A function that renders the "previous page" button. It delegates the rendering logic to the
//   `PrevPageLink` component, passing it the provided options.
//
// - NextPageLink: A function that renders the "next page" button. It delegates the rendering logic to the
//   `NextPageLink` component, passing it the provided options.
export const PaginatorTemplate = {
  layout:
    'PrevPageLink CurrentPageReport NextPageLink',
  PrevPageLink: (options: PaginatorPrevPageLinkOptions) => (
    <PrevPageLink options={options} />
  ),
  NextPageLink: (options: PaginatorNextPageLinkOptions) => (
    <NextPageLink options={options} />
  ),
};

/**
 * PrevPageLink
 * @param options
 * @returns CustomButton
 */
export const PrevPageLink: React.FC<{
  options: PaginatorPrevPageLinkOptions;
}> = ({ options }) => {
  return <CustomButton options={options} label="<<" />;
};

/**
 * NextPageLink
 * @param options
 * @returns CustomButton
 */
export const NextPageLink: React.FC<{
  options: PaginatorNextPageLinkOptions;
}> = ({ options }) => {
  return <CustomButton options={options} label=">>" />;
};

/**
 * CustomButton
 * @param options
 * @param label
 * @returns button
 */
export const CustomButton: React.FC<CustomButtonProps> = ({
  options,
  label,
}) => {
  return (
    <button
      type="button"
      className={classNames(options.className, 'border-round')}
      onClick={options.onClick}
      disabled={options.disabled}
    >
      <span className="p-3">{label}</span>
      <Ripple />
    </button>
  );
};
